/**
 * Implemented by a Transient Presenter whose `component`-lifetime Operation
 * handles must be disposed when the Component that resolved it unmounts.
 *
 * `useInjection` (core/providers/inversify-react.provider.tsx) resolves a
 * Transient Presenter once per mounted component and, until this existed,
 * never tore it down — a pending `component`-lifetime handle just kept
 * running after its Presenter was garbage-collected. Only classes explicitly
 * marked here are disposed; a plain Singleton Presenter or a class that
 * merely happens to expose a `dispose()` method is left alone (spec §16).
 */
export interface ComponentLifetime {
  readonly componentLifetime: true
  disposeComponent(): void
}

export function isComponentLifetime(value: unknown): value is ComponentLifetime {
  return (
    value !== null &&
    typeof value === 'object' &&
    'componentLifetime' in value &&
    (value as { componentLifetime: unknown }).componentLifetime === true &&
    'disposeComponent' in value &&
    typeof (value as { disposeComponent: unknown }).disposeComponent === 'function'
  )
}
